import * as React from 'react'
import {connect} from 'react-redux'
import {
  mkUntrustedPlayerEmail,
  mkUntrustedPlayerUsername,
} from '@kapitalist/contract/types'
import api from '~/api/endpoints'
import type {TSkin} from '~/game/skins'
import DashboardLayout from '~/layouts/DashboardLayout'
import type {TGameLobbySize} from '~/pages/GameLobby/types'
import SettingsUI from '~/pages/Settings/stateless'
import {setAuthenticatedPlayer} from '~/redux/reducers/authenticatedPlayer'
import type {ReduxState} from '~/redux/reducers/root'
import type {TBetaFeatureSettings} from '~/redux/reducers/settings'
import {
  setBetaFeatures,
  setDarkMode,
  setGameLobbySize,
  setNotifications,
  setSkin,
} from '~/redux/reducers/settings'
import {showSuccessToast} from '~/redux/reducers/toast'
import type {ISignedInRouteComponentProps} from '~/routing/Route/SignedIn'
import type {
  TUserNotificationPerSettingOptions,
  TUserNotificationSettings,
} from '~/utils/UserNotifications'

interface IReduxDispatchProps {
  setSkin: (skin: TSkin) => void
  setGameLobbySize: (gameLobbySize: TGameLobbySize) => void
  setNotifications: (notifications: TUserNotificationSettings) => void
  setBetaFeatures: (betaFeatures: TBetaFeatureSettings) => void
  setDarkMode: (darkMode: boolean) => void
  setAuthenticatedPlayer: typeof setAuthenticatedPlayer
  showSuccessToast: (message: string) => void
}
interface IReduxProps extends Pick<ReduxState, 'settingsState'> {}
interface IProps
  extends ISignedInRouteComponentProps, IReduxProps, IReduxDispatchProps {}

class SettingsPage extends React.Component<IProps> {
  render() {
    const player = this.props.authenticatedPlayer

    return (
      <DashboardLayout {...this.props}>
        <SettingsUI
          email={player.email}
          username={player.username}
          showSuccessToast={this.props.showSuccessToast}
          changeEmail={email =>
            api.account.changeEmail
              .execute({email: mkUntrustedPlayerEmail(email)}, {})
              .then(response => {
                this.props.setAuthenticatedPlayer(
                  response.authenticatedPlayerDetails
                )
                return response
              })
          }
          changeUsername={username =>
            api.account.changeUsername
              .execute({username: mkUntrustedPlayerUsername(username)}, {})
              .then(response => {
                this.props.setAuthenticatedPlayer(
                  response.authenticatedPlayerDetails
                )
                return response
              })
          }
          changePassword={params =>
            api.account.changePassword.execute(
              {
                currentPassword: params.currentPassword,
                newPassword: params.newPassword,
              },
              {}
            )
          }
          gameLobbySize={this.props.settingsState.gameLobbySize}
          setGameLobbySize={gameLobbySize => {
            return new Promise(resolve => {
              this.props.setGameLobbySize(gameLobbySize)
              resolve()
            })
          }}
          skin={this.props.settingsState.skin}
          setSkin={skin => {
            return new Promise(resolve => {
              this.props.setSkin(skin)
              resolve()
            })
          }}
          notifications={this.props.settingsState.notifications}
          setNotificationSetting={(
            setting: keyof TUserNotificationSettings,
            subsettings: TUserNotificationPerSettingOptions
          ) => {
            return new Promise(resolve => {
              this.props.setNotifications({
                ...this.props.settingsState.notifications,
                [setting]: subsettings,
              })
              resolve()
            })
          }}
          betaFeatures={this.props.settingsState.betaFeatures}
          setBetaFeatureSetting={(
            setting: keyof TBetaFeatureSettings,
            value: boolean
          ) => {
            return new Promise(resolve => {
              this.props.setBetaFeatures({
                ...this.props.settingsState.betaFeatures,
                [setting]: value,
              })
              resolve()
            })
          }}
          darkMode={this.props.settingsState.darkMode}
          setDarkMode={(isDarkMode: boolean) => {
            return new Promise(resolve => {
              this.props.setDarkMode(isDarkMode)
              resolve()
            })
          }}
        />
      </DashboardLayout>
    )
  }
}

const mapStateToProps = (state: ReduxState): IReduxProps => ({
  settingsState: state.settingsState,
})
const mapDispatchToProps = {
  setSkin,
  setGameLobbySize,
  setNotifications,
  setBetaFeatures,
  setDarkMode,
  setAuthenticatedPlayer,
  showSuccessToast,
}

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPage)
